import Constants from '@/Constants';
import { CourseLessonContentTO } from '@/models/CourseLessonContentTO';
import { CourseRequest } from '@/models/CourseResquest';
import { LessonData } from '@/models/LessonData';
import { LessonRequestData } from '@/models/LessonRequestData';
import { ListPostPutRequest, ListRequest } from '@/models/List';
import { Module } from '@/models/Module';
import { ScormStatus } from '@/models/ScormStatus';
import { ScormUpdateData } from '@/models/ScormUpdateData';
import { StartEnrollmentRequest } from '@/models/StartEnrollmentRequest';
import { UpdateCourseData } from '@/models/UpdateCourseData';
import http from '@/services/HttpService';
import { AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { call, delay, put, select } from 'redux-saga/effects';
import { PayloadAction } from 'typesafe-actions';
import _ from 'underscore';
import { ApplicationState, store } from '../index';
import { refreshProgramsEssentials, refreshProgramsFavorite, refreshProgramsInLastPublished, refreshProgramsInProgress } from '../program/saga';
import {
    addContentToCourseFailure,
    addContentToCourseSucess,
    archiveListFailure,
    archiveListSuccess,
    coursesCategorizedSuccess,
    coursesCompletedSuccess,
    coursesEssentialSuccess,
    coursesFailure,
    coursesFavoriteSuccess,
    coursesForYouSuccess,
    coursesHavingAContentSuccess,
    coursesInProgressSuccess,
    coursesLastPublishedSuccess,
    coursesLikeYouSuccess,
    coursesMostAccessedSuccess,
    coursesPublishedUserContentsSuccess,
    coursesSearchedSuccess,
    coursesTopRatedSuccess,
    coursesTrainingsSuccess,
    createCourseFailure,
    createCourseSuccess,
    deleteListFailure,
    deleteListSuccess,
    finishLessonFailure,
    finishLessonSuccess,
    getCourseFailure,
    getCourseStudentViewFailure,
    getCourseStudentViewSuccess,
    getCourseSuccess,
    getListFailure,
    getListsFailure,
    getListsSuccess,
    getListsSuccessFetchMore,
    getListSuccess,
    getScormPackageStatusFailure,
    getScormPackageStatusSuccess,
    increaseCourseViewsFailure,
    increaseCourseViewsSuccess,
    myCoursesSuccess,
    postListFailure,
    postListSuccess,
    publishListSuccess,
    putListFailure,
    putListSuccess,
    refreshList,
    startEnrollmentFailure,
    startEnrollmentSuccess,
    startLessonFailure,
    startLessonSuccess,
    toggleListVisibilityFailure,
    toggleListVisibilitySuccess,
    updateCourseFailure,
    updateCourseStudentViewFailure,
    updateCourseStudentViewRequest as updateCourseStudentViewRequestAction,
    updateCourseStudentViewSuccess,
    updateCourseSuccess,
    updateScormPackageFailure,
    updateScormPackageSuccess,
} from './action';
import { CourseStudentView, ListTypes } from './types';

export function* getList(action: PayloadAction<ListTypes.GET_LIST_REQUEST, number>) {
    try {
        const id = action.payload;
        const { data } = yield call(http.get, `${Constants.LMS_COURSES_URI}/${id}`);
        yield put(getListSuccess(data));
    } catch (err) {
        yield put(getListFailure(err));
    }
}

export function* getLists(action: PayloadAction<ListTypes.GET_LISTS_REQUEST, ListRequest>) {
    try {
        const {
            applicationId,
            orgId,
            maxWorkload,
            mediaType,
            minWorkload,
            name,
            status,
            visibility,
            onlyHasProgramClass,
            orderBy,
            locale,
            moduleId,
            offset,
            fetching,
            limit,
            notInProgramId,
            categoryIds,
            type,
        } = action.payload;

        const { data, headers } = yield call(
            http.get,
            `${
                Constants.LMS_COURSES_URI
            }?withLessonAmount=true&withClassAmount=true&applicationId=${applicationId}&organizationId=${orgId}&onlyHasProgramClass=${onlyHasProgramClass}${
                name ? `&name=${name}` : ''
            }${mediaType ? `&mediaType=${mediaType}` : ''}${minWorkload ? `&minWorkload=${minWorkload}` : ''}${
                maxWorkload ? `&maxWorkload=${maxWorkload}` : ''
            }${visibility ? `&visibility=${visibility}` : ''}${status ? `&status=${status}` : ''}${orderBy ? `&orderBy=${orderBy}` : ''}${
                locale ? `&locale=${locale}` : ''
            }${moduleId ? `&moduleId=${moduleId}` : ''}${_.isNumber(offset) ? `&offset=${offset}` : ''}${_.isNumber(limit) ? `&limit=${limit}` : ''}${
                notInProgramId ? `&notInProgramId=${notInProgramId}` : ''
            }${categoryIds ? `&categoryIds=${categoryIds}` : ''}${type ? `&type=${type}` : ''}`,
        );
        if (fetching) yield put(getListsSuccessFetchMore(data, parseInt(headers.total)));
        else yield put(getListsSuccess(data, parseInt(headers.total)));
    } catch (err) {
        yield put(getListsFailure(err));
    }
}

export function* postList(action: PayloadAction<ListTypes.POST_LIST_REQUEST, ListPostPutRequest>) {
    const {
        locale: {
            data: { toastMessages },
        },
    } = store.getState();
    try {
        const { list, willClose, history } = action.payload;
        const { data } = yield call(http.post, `${Constants.LMS_COURSES_URI}`, list);
        yield put(postListSuccess(data));
        toast.success(toastMessages.LIST_SAVE_SUCCESS);
        if (willClose) {
            history.push('/contents/playlist/lists');
        } else {
            history.push(`/lists/${data.id}`);
        }
    } catch (err) {
        toast.error(toastMessages.LIST_SAVE_ERROR);
        yield put(postListFailure(err));
    }
}

export function* putList(action: PayloadAction<ListTypes.PUT_LIST_REQUEST, ListPostPutRequest>) {
    const {
        locale: {
            data: { toastMessages },
        },
    } = store.getState();
    try {
        const { list, willClose, history } = action.payload;
        const { id } = list;
        yield call(http.put, `${Constants.LMS_COURSES_URI}/${id}`, list);
        yield put(putListSuccess());
        toast.success(toastMessages.LIST_SAVE_SUCCESS);
        if (willClose) {
            history.push('/contents/playlist/lists');
        } else {
            history.push(`/lists/${id}`);
        }
    } catch (err) {
        toast.error(toastMessages.LIST_SAVE_ERROR);
        yield put(putListFailure(err));
    }
}

export function* deleteList(action: PayloadAction<ListTypes.DELETE_LIST_REQUEST, number>) {
    const {
        locale: {
            data: { toastMessages },
        },
    } = store.getState();
    try {
        yield call(http.delete, `${Constants.LMS_COURSES_URI}/${action.payload}`);
        toast.success(toastMessages.CONTENT_REMOVE_SUCCESS);
        yield put(deleteListSuccess(action.payload));
        yield put(refreshList());
    } catch (err) {
        toast.error(toastMessages.CONTENT_REMOVE_ERROR);
        yield put(deleteListFailure(err));
    }
}

export function* publishList(action: PayloadAction<ListTypes.PUBLISH_LIST_REQUEST, number>) {
    const {
        locale: {
            data: { toastMessages },
        },
    } = store.getState();
    try {
        yield call(http.post, `${Constants.LMS_COURSES_URI}/${action.payload}/publish`);
        yield put(publishListSuccess(action.payload));
    } catch (err) {
        toast.error(toastMessages.LIST_PUBLISHED_ERROR);
        yield put(putListFailure(err));
    }
}

export function* archiveList(action: PayloadAction<ListTypes.ARCHIVE_LIST_REQUEST, { data: number; runGetCourses: boolean; showToast: boolean }>) {
    const { data, runGetCourses, showToast } = action.payload;
    const {
        locale: {
            data: { toastMessages },
        },
    } = store.getState();
    try {
        const {
            identity: {
                data: {
                    user: { orgId, applicationId },
                },
            },
        } = store.getState();

        yield call(http.post, `${Constants.LMS_COURSES_URI}/${data}/archive`);
        if (showToast) toast.success(toastMessages.LIST_ARCHIVED_SUCCESS);
        yield put(archiveListSuccess(action.payload.data));

        if (runGetCourses) {
            const { data, headers } = yield call(
                http.get,
                `${Constants.LMS_COURSES_URI}?applicationId=${applicationId}&organizationId=${orgId}&status=PUBLISHED`,
            );
            yield put(getListsSuccess(data, parseInt(headers.total)));
        }
    } catch (err) {
        if (showToast) toast.error(toastMessages.LIST_ARCHIVED_ERROR);
        yield put(archiveListFailure(err));
    }
}

export function* toggleListVisibility(action: PayloadAction<ListTypes.TOGGLE_LIST_VISIBILITY_REQUEST, number>) {
    try {
        yield call(http.post, `${Constants.LMS_COURSES_URI}/${action.payload}/toggle-visibility`);
        yield put(toggleListVisibilitySuccess(action.payload));
    } catch (err) {
        yield put(toggleListVisibilityFailure());
    }
}

let requestCounter: number = 0;
export function* coursesRequest(action: PayloadAction<ListTypes.COURSES_REQUEST, CourseRequest>) {
    try {
        yield delay(requestCounter++ * Constants.SAGA_DELAY);

        const {
            personId,
            paginationData,
            courseStatus,
            lessonStatus,
            categoryId,
            courseIds,
            courseId,
            contentId,
            term,
            excludeEnrollments,
            onlyMine,
            ignoreLocale,
            onlyMandatoryClasses,
            classEnrollmentStatus,
            moduleIds,
            ownerId,
            excludeExpiredAndNotFinished,
            section,
            visibility,
            locales,
        } = action.payload;

        let path: string = Constants.LMS_STUDENT_COURSES_URI.replace('%s', `${personId}`);
        let queryParams: string = `?offset=${paginationData.offset}&limit=${paginationData.limit}`;

        if (paginationData.orderBy !== undefined) {
            queryParams += `&orderBy=${paginationData.orderBy}`;
        }

        if (paginationData.courseRequestSuccessType === ListTypes.SEARCHED_SUCCESS) {
            if (!term) {
                return;
            }

            path = Constants.CAST_COURSES_SEARCH_URI;
            queryParams += `&term=${term}`;
        }

        if (courseStatus !== undefined) {
            queryParams += `&courseStatus=${courseStatus}`;
        }

        if (visibility !== undefined) {
            queryParams += `&visibility=${visibility}`;
        }

        if (lessonStatus !== undefined) {
            queryParams += `&lessonStatus=${lessonStatus}`;
        }

        if (categoryId !== undefined) {
            queryParams += `&categoryId=${categoryId}`;
        }

        if (courseIds !== undefined) {
            queryParams += `&courseIds=${courseIds}`;
        }

        if (courseId !== undefined) {
            queryParams += `&courseId=${courseId}`;
        }

        if (contentId !== undefined) {
            queryParams += `&contentId=${contentId}`;
        }

        if (excludeEnrollments !== undefined) {
            queryParams += `&excludeEnrollments=${excludeEnrollments}`;
        }

        if (onlyMine !== undefined) {
            queryParams += `&onlyMine=${onlyMine}`;
        }

        if (ignoreLocale !== undefined) {
            queryParams += `&ignoreLocale=${ignoreLocale}`;
        }

        if (onlyMandatoryClasses !== undefined) {
            queryParams += `&onlyMandatoryClasses=${onlyMandatoryClasses}`;
        }

        if (classEnrollmentStatus !== undefined) {
            queryParams += `&classEnrollmentStatus=${classEnrollmentStatus}`;
        }

        if (ownerId !== undefined) {
            queryParams += `&ownerId=${ownerId}`;
        }

        if (moduleIds !== undefined) {
            queryParams += `&moduleIds=${moduleIds}`;
        }

        if (excludeExpiredAndNotFinished !== undefined) {
            queryParams += `&excludeExpiredAndNotFinished=${excludeExpiredAndNotFinished}`;
        }

        if (section !== undefined) {
            queryParams += `&section=${section}`;
        }

        if (locales !== undefined) {
            queryParams += `&locales=${locales}`;
        }

        let response = {
            data: [],
            headers: {
                total: '0',
                totaltoexpire: '0',
            },
        };

        const request: AxiosRequestConfig = { headers: { requestSuccessType: paginationData.courseRequestSuccessType } };

        if (paginationData.doRequest) {
            response = yield call(http.get, path + queryParams, request);
        }

        switch (request.headers.requestSuccessType) {
            case ListTypes.LAST_PUBLISHED_SUCCESS:
                yield put(coursesLastPublishedSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ListTypes.MOST_ACCESSED_SUCCESS:
                yield put(coursesMostAccessedSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ListTypes.IN_PROGRESS_SUCCESS:
                yield put(coursesInProgressSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ListTypes.COMPLETED_SUCCESS:
                yield put(coursesCompletedSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ListTypes.TOP_RATED_SUCCESS:
                yield put(coursesTopRatedSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ListTypes.CATEGORIZED_SUCCESS:
                yield put(coursesCategorizedSuccess(response.data, parseInt(response.headers.total), categoryId));
                break;
            case ListTypes.FAVORITES_SUCCESS:
                yield put(coursesFavoriteSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ListTypes.FOR_YOU_SUCCESS:
                yield put(coursesForYouSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ListTypes.LIKE_YOU_SUCCESS:
                yield put(coursesLikeYouSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ListTypes.COURSES_HAVING_A_CONTENT_SUCCESS:
                yield put(coursesHavingAContentSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ListTypes.SEARCHED_SUCCESS:
                yield put(coursesSearchedSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ListTypes.MY_COURSES_SUCCESS:
                yield put(myCoursesSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ListTypes.ESSENTIAL_SUCCESS:
                yield put(coursesEssentialSuccess(response.data, parseInt(response.headers.total), parseInt(response.headers.totaltoexpire)));
                break;
            case ListTypes.PUBLISHED_USER_CONTENTS_SUCCESS:
                yield put(coursesPublishedUserContentsSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ListTypes.TRAININGS_SUCCESS:
                yield put(coursesTrainingsSuccess(response.data, parseInt(response.headers.total)));
                break;
            default:
                break;
        }
    } catch (error) {
        yield put(coursesFailure());
    } finally {
        requestCounter--;
    }
}

export function* startLessonRequest(action: PayloadAction<ListTypes.START_LESSON_REQUEST, LessonRequestData>) {
    try {
        const { lessonId, personId, courseId, lessonStartAndFinishData } = action.payload;
        yield call(http.post, Constants.START_LESSON_URI.replace('%s', lessonId.toString()), lessonStartAndFinishData);
        yield put(startLessonSuccess());
        yield put(updateCourseStudentViewRequestAction({ personId, courseId, classEnrollmentId: lessonStartAndFinishData.classEnrollmentId }));
    } catch (error) {
        yield put(startLessonFailure());
    }
}

export function* finishLessonRequest(action: PayloadAction<ListTypes.FINISH_LESSON_REQUEST, LessonRequestData>) {
    try {
        const { lessonId, personId, courseId, lessonStartAndFinishData } = action.payload;
        yield call(http.post, Constants.FINISH_LESSON_URI.replace('%s', lessonId.toString()), lessonStartAndFinishData);
        yield put(finishLessonSuccess());
        yield put(updateCourseStudentViewRequestAction({ personId, courseId, classEnrollmentId: lessonStartAndFinishData.classEnrollmentId }));
    } catch (error) {
        yield put(finishLessonFailure());
    }
}

export function* updateCourseStudentViewRequest(action: PayloadAction<ListTypes.UPDATE_COURSE_STUDENT_VIEW_REQUEST, UpdateCourseData>) {
    try {
        const { personId, courseId, classEnrollmentId } = action.payload;
        let path = Constants.COURSE_STUDENT_VIEW_URI.replace('%p', personId.toString()).replace('%c', courseId.toString());

        let refreshPrograms: boolean = false;
        if (classEnrollmentId) {
            path += `?classEnrollmentId=${classEnrollmentId}`;
            refreshPrograms = true;
        }

        const response = yield call(http.get, path);
        const courseStudentView: CourseStudentView = response.data;
        yield put(updateCourseStudentViewSuccess(courseStudentView));

        if (refreshPrograms) {
            const modules: Module[] = yield select((state: ApplicationState) => state.appOrganization.data.modules);
            yield call(refreshProgramsInProgress, personId, classEnrollmentId, modules);
            yield call(refreshProgramsInLastPublished, personId, classEnrollmentId, modules);
            yield call(refreshProgramsFavorite, personId, classEnrollmentId, modules);
            yield call(refreshProgramsEssentials, personId, classEnrollmentId, modules);
        }
    } catch (error) {
        yield put(updateCourseStudentViewFailure());
    }
}

export function* getCourseStudentViewRequest(action: PayloadAction<ListTypes.GET_COURSE_STUDENT_VIEW_REQUEST, CourseRequest>) {
    try {
        const { personId, courseId, moduleId, classEnrollmentId } = action.payload;
        const flowLmsWithEnrollmentId: string = `${Constants.FLOWLMS_COURSE_STUDENT_VIEW_URI}?classEnrollmentId=${classEnrollmentId}`;

        let path = moduleId === Constants.LMS_MODULE_ID ? flowLmsWithEnrollmentId : Constants.COURSE_STUDENT_VIEW_URI;
        path = path.replace('%p', personId.toString()).replace('%c', courseId.toString());

        const response = yield call(http.get, path);
        yield put(getCourseStudentViewSuccess(response.data as CourseStudentView));
    } catch (error) {
        yield put(getCourseStudentViewFailure());
    }
}

export function* createCourseRequest(action: PayloadAction<ListTypes.CREATE_COURSE_REQUEST, CourseLessonContentTO>) {
    try {
        const response = yield call(http.post, Constants.LMS_COURSES_URI, action.payload);
        yield put(createCourseSuccess(response.data));
    } catch (error) {
        yield put(createCourseFailure(error));
    }
}

export function* updateCourseRequest(action: PayloadAction<ListTypes.UPDATE_COURSE_REQUEST, CourseLessonContentTO>) {
    try {
        const { id } = action.payload;
        const response = yield call(http.put, `${Constants.LMS_COURSES_URI}/${id}`, action.payload);
        yield put(updateCourseSuccess(response.data));
    } catch (error) {
        yield put(updateCourseFailure(error));
    }
}

export function* addContentToCourseRequest(action: PayloadAction<ListTypes.ADD_CONTENT_TO_COURSE_REQUEST, LessonData[]>) {
    try {
        if (action.payload.length > 0) {
            yield call(http.post, `${Constants.LESSONS_URI}/batch-create`, action.payload);
            yield put(addContentToCourseSucess());
        }
    } catch (err) {
        yield put(addContentToCourseFailure(err));
    }
}

export function* getCourseRequest(action: PayloadAction<ListTypes.GET_COURSE_REQUEST, { id: number; ignoreLocale: boolean }>) {
    try {
        const { id, ignoreLocale } = action.payload;
        let queryParam: string = '';
        if (ignoreLocale) {
            queryParam = `?ignoreLocale=${ignoreLocale}`;
        }
        const { data } = yield call(http.get, `${Constants.LMS_COURSES_URI}/${id}${queryParam}`);
        yield put(getCourseSuccess(data));
    } catch (err) {
        yield put(getCourseFailure(err));
    }
}

export function* updateScormPackageRequest(action: PayloadAction<ListTypes.UPDATE_SCORM_PACKAGE_REQUEST, ScormUpdateData>) {
    const {
        locale: {
            data: { toastMessages },
        },
    } = store.getState();
    try {
        const { courseId } = action.payload;
        yield call(http.post, `${Constants.LMS_COURSES_URI}/${courseId}/update-scorm`, action.payload);
        yield put(updateScormPackageSuccess());
    } catch (err) {
        toast.error(
            err.response.status === Constants.statusCode.ENTITY_NOT_FOUND
                ? toastMessages.UPDATE_SCORM_PACKAGE_IDENTIFIER_ERROR
                : err.response.status === Constants.statusCode.LESSONS_SIZE_INVALID_STATUS
                ? toastMessages.UPDATE_SCORM_PACKAGE_SCOS_SIZE_ERROR
                : toastMessages.UPDATE_SCORM_PACKAGE_ERROR,
        );
        yield put(updateScormPackageFailure(err));
    }
}

export function* getScormPackageStatusRequest(
    action: PayloadAction<ListTypes.GET_SCORM_PACKAGE_STATUS_REQUEST, { courseId: number; showToast: boolean }>,
) {
    const {
        locale: {
            data: { toastMessages },
        },
    } = store.getState();
    try {
        const { courseId, showToast } = action.payload;
        const response = yield call(http.get, `${Constants.LMS_COURSES_URI}/${courseId}/scorm-status`);
        const scormStatus: ScormStatus = response.data;

        if (showToast) {
            switch (scormStatus.status) {
                case Constants.scormStatus.SUCCESS:
                    toast.success(toastMessages.SCORM_PACKAGE_PROCESS_SUCCESS);
                    break;
                case Constants.scormStatus.ERROR:
                    toast.success(toastMessages.SCORM_PACKAGE_PROCESS_ERROR);
                    break;
            }
        }

        yield put(getScormPackageStatusSuccess(scormStatus));
    } catch (err) {
        yield put(getScormPackageStatusFailure(err));
    }
}

export function* increaseCourseViewsRequest(action: PayloadAction<ListTypes.INCREASE_COURSE_VIEWS_REQUEST, number>) {
    try {
        yield call(http.post, Constants.LMS_COURSES_VIEWS.replace('%s', action.payload.toString()));
        yield put(increaseCourseViewsSuccess());
    } catch (error) {
        yield put(increaseCourseViewsFailure());
    }
}

export function* startEnrollmentRequest(action: PayloadAction<ListTypes.START_ENROLLMENT_REQUEST, StartEnrollmentRequest>) {
    try {
        const { personId, courseId, courseStartData } = action.payload;

        const path = Constants.LMS_START_ENROLLMENT.replace('%p', personId.toString()).replace('%c', courseId.toString());

        const { data } = yield call(http.post, path, courseStartData);

        yield put(startEnrollmentSuccess(data));
    } catch (error) {
        yield put(startEnrollmentFailure());
    }
}
